import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, TextField, Typography, MenuItem } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { leagueApi, matchApi, stadiumApi } from '../../services/footballApi';

const statusOptions = [
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'LIVE', label: 'Live' },
  { value: 'HALF_TIME', label: 'Half Time' },
  { value: 'FULL_TIME', label: 'Full Time' },
  { value: 'EXTRA_TIME', label: 'Extra Time' },
  { value: 'PENALTIES', label: 'Penalties' },
  { value: 'POSTPONED', label: 'Postponed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const MatchForm: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [leagues, setLeagues] = useState<{ id: number; name: string }[]>([]);
  const [stadiums, setStadiums] = useState<{ id: number; name: string }[]>([]);
  const [form, setForm] = useState({
    name: '',
    league: '',
    match_status: '',
    start_date: '',
    venue: '',
  });

  useEffect(() => {
    leagueApi.getAll().then(res => setLeagues(res.data)).catch(() => setLeagues([]));
    stadiumApi.getAll().then(res => setStadiums(res.data)).catch(() => setStadiums([]));
    if (isEdit) {
      setLoading(true);
      matchApi.getById(parseInt(id || '')).then(res => {
        setForm({
          name: res.data.name || '',
          league: res.data.league ? res.data.league.id : '',
          match_status: res.data.match_status || '',
          start_date: res.data.start_date ? res.data.start_date.slice(0, 16) : '',
          venue: res.data.venue ? res.data.venue.id : '',
        });
      }).catch(() => setError('Failed to load match')).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = { ...form, league: form.league || null, venue: form.venue || null };
      if (isEdit) {
        await matchApi.update(parseInt(id || ''), payload);
        setSuccess('Match updated successfully');
      } else {
        await matchApi.create(payload);
        setSuccess('Match created successfully');
        setForm({ name: '', league: '', match_status: '', start_date: '', venue: '' });
      }
      setTimeout(() => navigate('/football/matches'), 1000);
    } catch (err: any) {
      setError('Failed to save match');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxWidth={500} mx="auto" mt={4}>
      <Typography variant="h5" mb={2}>{isEdit ? 'Edit' : 'Create'} Football Match</Typography>
      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth />
          <TextField select label="League" name="league" value={form.league} onChange={handleChange} required fullWidth>
            <MenuItem value="">None</MenuItem>
            {leagues.map(league => <MenuItem key={league.id} value={league.id}>{league.name}</MenuItem>)}
          </TextField>
          <TextField select label="Status" name="match_status" value={form.match_status} onChange={handleChange} required fullWidth>
            {statusOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
          </TextField>
          <TextField label="Start Date" name="start_date" type="datetime-local" value={form.start_date} onChange={handleChange} InputLabelProps={{ shrink: true }} required fullWidth />
          <TextField select label="Venue" name="venue" value={form.venue} onChange={handleChange} fullWidth>
            <MenuItem value="">None</MenuItem>
            {stadiums.map(stadium => <MenuItem key={stadium.id} value={stadium.id}>{stadium.name}</MenuItem>)}
          </TextField>
          {error && <Typography color="error">{error}</Typography>}
          {success && <Typography color="success.main">{success}</Typography>}
          <Button type="submit" variant="contained" disabled={loading}>{isEdit ? 'Update' : 'Create'}</Button>
        </Stack>
      </form>
    </Box>
  );
};

export default MatchForm; 